// Utility to get global page size from Redux store
import { useSelector } from 'react-redux';

import { PAGE_LIMIT } from '../constants';

/**
 * Get the global page size from Redux state
 * This can be used by components that need to know the page size before mounting tables
 * 
 * @param {Object} state - Redux state
 * @returns {number} - The global page size or default PAGE_LIMIT
 */
export const getGlobalPageSize = (state) => {
  return state?.tableReducer?.globalPageSize || PAGE_LIMIT;
};

/**
 * Hook to get global page size from Redux store
 * Use this in components that need the page size for data fetching
 */
export const useGlobalPageSize = () => {
  return useSelector(getGlobalPageSize);
};

const SORT_TYPE_ASC_VALUES = ['asc', 'ascending'];
const SORT_TYPE_DESC_VALUES = ['desc', 'descending'];

export const normalizeSortTypeForRequest = (sortType) => {
  const normalized = String(sortType || '').toLowerCase();
  if (SORT_TYPE_ASC_VALUES.includes(normalized)) {
    return 'ascending';
  }
  if (SORT_TYPE_DESC_VALUES.includes(normalized)) {
    return 'descending';
  }
  return undefined;
};

export const preserveHistoryStateForUrlUpdate = (nextUrl) => {
  if (typeof window === 'undefined') {
    return nextUrl;
  }

  const currentState = window.history.state && typeof window.history.state === 'object'
    ? window.history.state
    : {};

  const preservedState = {
    ...currentState,
    tableScrollPositions: currentState.tableScrollPositions || {},
  };

  window.history.replaceState(preservedState, '', nextUrl);
  return nextUrl;
};

export const updateUrlParamsPreservingHistoryState = (paramsToSet = {}, paramsToDelete = []) => {
  if (typeof window === 'undefined') {
    return '';
  }

  const params = new URLSearchParams(window.location.search);

  Object.entries(paramsToSet).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      params.delete(key);
      return;
    }

    params.set(key, String(value));
  });

  paramsToDelete.forEach(key => {
    params.delete(key);
  });

  const query = params.toString();
  const nextUrl = query
    ? `${window.location.pathname}?${query}${window.location.hash}`
    : `${window.location.pathname}${window.location.hash}`;

  return preserveHistoryStateForUrlUpdate(nextUrl);
};

export const getSearchParamFromUrl = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const params = new URLSearchParams(window.location.search);
  return params.get('search') || '';
};

export const saveSearchParamToUrl = (search, resetOffset = true) => {
  return updateUrlParamsPreservingHistoryState(
    { search: search || null },
    resetOffset ? ['offset'] : []
  );
};

export const hydrateTableRequestFromUrl = (request, defaultLimit = PAGE_LIMIT) => {
  if (!request || typeof window === 'undefined') {
    return request;
  }

  const params = new URLSearchParams(window.location.search);
  const limit = Number(request.limit) > 0 ? Number(request.limit) : defaultLimit;

  const offsetFromUrl = Number.parseInt(params.get('offset'), 10);
  if (Number.isFinite(offsetFromUrl) && offsetFromUrl >= 0) {
    request.offset = offsetFromUrl;
  } else {
    const pageFromUrl = Number.parseInt(params.get('page'), 10);
    if (Number.isFinite(pageFromUrl) && pageFromUrl >= 0) {
      request.offset = pageFromUrl * limit;
    }
  }

  const sortColumn = params.get('sortColumn');
  const sortType = normalizeSortTypeForRequest(params.get('sortType'));

  if (sortColumn) {
    request.sortColumn = sortColumn;
    request.sortType = sortType || 'ascending';
  }

  return request;
};